import React, { useMemo } from 'react';
import { Button, Checkbox, DatePicker, Space } from 'antd';
import moment from 'moment';
import { FOREVER_UNTIL_ISO, isForeverUntil } from '@src/components/common/RuleStatus';

export type TimeWindowValue = [moment.Moment, moment.Moment];

// End value for a "mute forever" (no end date) window.
const foreverEnd = () => moment.utc(FOREVER_UNTIL_ISO);

interface RuleTimeWindowPickerProps {
  value?: TimeWindowValue;
  onChange?: (value: TimeWindowValue) => void;
  // Optional cap in hours. When set, quick-selects longer than the cap are
  // hidden and a hint is shown (Global Mute passes 24).
  maxDurationHours?: number;
  disabled?: boolean;
  label?: string;
}

const showTime = { format: 'HH:mm' };

const disablePastDates = (date: any) => !!date && moment(date).isBefore(moment(), 'day');

// Forward quick-selects: each sets [now, now + duration].
const PRESETS: { label: string; hours: number }[] = [
  { label: 'Next 2h', hours: 2 },
  { label: 'Next 6h', hours: 6 },
  { label: 'Next 12h', hours: 12 },
  { label: 'Next 24h', hours: 24 },
];

// Controlled time-window picker with forward quick-selects. Not form-bound, so
// it can be dropped into any Form.Item (antd injects value/onChange) or used
// standalone. Shipped calendar fixes carried over: bounded height (sizes to
// content) and no orphaned popup — antd DatePicker handles outside-click/Escape.
const RuleTimeWindowPicker = ({
  value,
  onChange,
  maxDurationHours,
  disabled,
  label,
}: RuleTimeWindowPickerProps) => {
  const [from, to] = value || [];

  const isForever = !!to && isForeverUntil(to.toISOString());

  const tzLabel = `UTC${(from || moment()).format('Z')}`;

  const presets = useMemo(
    () => PRESETS.filter((p) => !maxDurationHours || p.hours <= maxDurationHours),
    [maxDurationHours],
  );

  // "Mute forever" is only offered for uncapped windows (Global Mute passes a
  // cap, so it can't be set to never end).
  const allowForever = !maxDurationHours;

  const applyPreset = (hours: number) => {
    const start = moment();
    onChange?.([start, start.clone().add(hours, 'hour')]);
  };

  const handleStart = (next: moment.Moment | null) => {
    if (!next) return;
    onChange?.([next, isForever ? foreverEnd() : to || next.clone().add(2, 'hour')]);
  };

  const handleEnd = (next: moment.Moment | null) => {
    if (!next) return;
    onChange?.([from || moment(), next]);
  };

  const toggleForever = (checked: boolean) => {
    const start = from || moment();
    onChange?.([start, checked ? foreverEnd() : start.clone().add(2, 'hour')]);
  };

  const exceedsCap =
    !isForever &&
    !!maxDurationHours &&
    !!from &&
    !!to &&
    to.diff(from, 'hours', true) > maxDurationHours;
  const endBeforeStart = !isForever && !!from && !!to && !to.isAfter(from);

  return (
    <div>
      <div style={{ marginBottom: 8, fontWeight: 500 }}>
        {label || 'Time Window'}{' '}
        <span style={{ color: '#8c8c8c', fontWeight: 400 }}>({tzLabel})</span>
      </div>

      <Space size={8} style={{ marginBottom: 12, flexWrap: 'wrap' }}>
        {presets.map((p) => (
          <Button
            key={p.hours}
            size="small"
            disabled={disabled}
            onClick={() => applyPreset(p.hours)}
          >
            {p.label}
          </Button>
        ))}
      </Space>

      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <DatePicker
          style={{ width: '100%' }}
          showTime={showTime}
          format="YYYY-MM-DD HH:mm"
          allowClear={false}
          disabled={disabled}
          disabledDate={disablePastDates}
          value={from}
          placeholder="Start"
          onChange={handleStart as any}
        />
        <DatePicker
          style={{ width: '100%' }}
          showTime={showTime}
          format="YYYY-MM-DD HH:mm"
          allowClear={false}
          disabled={disabled || isForever}
          disabledDate={disablePastDates}
          value={isForever ? undefined : to}
          placeholder={isForever ? 'No end date (forever)' : 'End'}
          onChange={handleEnd as any}
        />

        {allowForever ? (
          <Checkbox
            checked={isForever}
            disabled={disabled}
            onChange={(e) => toggleForever(e.target.checked)}
          >
            No end date (mute forever)
          </Checkbox>
        ) : null}
      </Space>

      {endBeforeStart ? (
        <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 6 }}>End must be after start.</div>
      ) : null}
      {exceedsCap ? (
        <div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 6 }}>
          Window is capped at {maxDurationHours} hours.
        </div>
      ) : null}
    </div>
  );
};

export default RuleTimeWindowPicker;
